import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { Panel } from '../../components/primitives/Panel';
import { SMLLink } from '../../components/SMLLink/SMLLink';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { countWorking } from '../work/studioWorkModel';
import { workFilesOf, workProgressQuery } from '../work/workProgressLink';
import { ReleaseTabProps } from './releaseTabProps';
import { releaseMessages } from './releaseMessages';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './ReleasePage.module.scss';
/* eslint-enable css-modules/no-unused-class */

type ReleaseWorkingBannerProps = Pick<ReleaseTabProps, 'item' | 'release'>;

/**
 * "2 assets are working for Swamps. They show up under "Just made" when they are ready. You can
 * leave this page." with "See the work" (design e4). It shows while something of the song works.
 * The link opens the Work page with the work of the song (owner, 2026-10-11; design assets-rethink
 * f4); when one file works, its row has the mark.
 */
const ReleaseWorkingBannerComponent: React.FC<ReleaseWorkingBannerProps> = ({ item, release }) => {
  const intl = useIntl();
  const working = useMemo(() => countWorking(release.assets, release.reels), [release.assets, release.reels]);
  const params = useMemo(
    () =>
      workProgressQuery(
        item.uuid,
        'working',
        workFilesOf({ assets: release.assets, reels: release.reels, state: 'working' })
      ),
    [item.uuid, release.assets, release.reels]
  );

  if (working === 0) {
    return null;
  }
  return (
    <Panel pad className={styles.banner} role="status" data-testid="release-working-banner">
      <span className={styles.bannerDot} aria-hidden="true" />
      <span className={styles.bannerText}>
        {intl.formatMessage(releaseMessages.workingBanner, { count: working, title: item.title })}
      </span>
      <SMLLink to={SMLRoutes.promoteWork} params={params}>
        {intl.formatMessage(releaseMessages.seeWork)}
      </SMLLink>
    </Panel>
  );
};

export const ReleaseWorkingBanner = TypedMemo(ReleaseWorkingBannerComponent);
